import React, { useCallback, useMemo, useState } from 'react';
import {
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  FormControlLabel,
  IconButton,
  MenuItem,
  Paper,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import Add from '@mui/icons-material/Add';
import Delete from '@mui/icons-material/Delete';
import { GRAPHQL_AUTH_MODE } from '@aws-amplify/api-graphql';
import { getGQLAPI, IPortal, useStore } from '@mcn-platform/models';
import { commerceSearch as commerceSearchQuery } from 'graphql/queries';

/** Entity types when not in product-only mode (object_type filter). */
const ENTITY_TYPES = [
  '',
  'Course',
  'Document',
  'Instructor',
  'Meeting',
  'Microcast',
  'Program',
  'DiscussionPost',
  'DiscussionTopic',
  'MeetingRegistration',
  'PortalKicker',
  'Product',
  'Category',
  'WebPage',
  'Organization',
] as const;

function formatError(err: unknown): string {
  if (err instanceof Error) return err.message;
  if (typeof err === 'string') return err;
  if (Array.isArray(err)) {
    const msgs = err
      .map((e) => (e && typeof e === 'object' && 'message' in e ? String((e as { message: unknown }).message) : null))
      .filter(Boolean);
    if (msgs.length) return msgs.join(' ');
  }
  if (err && typeof err === 'object') {
    if ('errors' in err && Array.isArray((err as { errors: unknown[] }).errors)) {
      return formatError((err as { errors: unknown[] }).errors);
    }
    if ('message' in err && typeof (err as { message: unknown }).message === 'string') {
      return (err as { message: string }).message;
    }
  }
  return 'Search failed';
}

type OptionPair = { name: string; value: string };

function portalLibraryIds(portal: IPortal): string[] {
  const p = portal as { defaultLibraryID?: string; libraries?: string[] };
  const ids: string[] = [];
  if (p.defaultLibraryID) ids.push(String(p.defaultLibraryID));
  if (Array.isArray(p.libraries)) ids.push(...p.libraries.map(String));
  return [...new Set(ids.filter(Boolean))];
}

const PgVectorSearchPanel = ({ portal }: { portal: IPortal }) => {
  const { currentOrganization } = useStore();
  const organizationID = (currentOrganization?.id ?? '').trim();
  const cfg = (portal.clientAIConfig ?? {}) as {
    filterMode?: string;
    maxResults?: number;
    useHybridSearch?: boolean;
  };
  const filterMode = cfg.filterMode === 'library' ? 'library' : 'organization';
  const libraryIds = useMemo(() => portalLibraryIds(portal), [portal]);
  const useHybridSearch = cfg.useHybridSearch !== false;

  const [queryText, setQueryText] = useState('');
  const [productSearch, setProductSearch] = useState(false);
  const [entityType, setEntityType] = useState<string>('');
  const [optionRows, setOptionRows] = useState<OptionPair[]>([{ name: '', value: '' }]);
  const [limit, setLimit] = useState(String(cfg.maxResults ?? 8));
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [rawResult, setRawResult] = useState<{
    references: unknown[];
    formattedText: string;
  } | null>(null);

  const productOptionsJson = useMemo(() => {
    const o: Record<string, string> = {};
    for (const { name, value } of optionRows) {
      const k = name.trim();
      const v = value.trim();
      if (k && v) o[k] = v;
    }
    return Object.keys(o).length > 0 ? JSON.stringify(o) : undefined;
  }, [optionRows]);

  const objectTypesForRequest = useMemo(() => {
    if (productSearch) return ['Product'];
    const t = entityType.trim();
    return t ? [t] : [];
  }, [productSearch, entityType]);
  const libraryModeBlocked = filterMode === 'library' && libraryIds.length === 0;
  const canSearch = Boolean(organizationID && queryText.trim() && !libraryModeBlocked);

  const runSearch = useCallback(async () => {
    if (!organizationID) {
      setError('No organization selected. Choose an organization in the admin header.');
      return;
    }
    if (!queryText.trim()) {
      setError('Enter search text (embedded like the shopping assistant).');
      return;
    }
    if (libraryModeBlocked) {
      setError('Portal filter mode is Library but no library IDs on this portal. Add libraries or switch to Organization in AI Config → Tools.');
      return;
    }
    setLoading(true);
    setError(null);
    setRawResult(null);
    try {
      const lim = Math.min(100, Math.max(1, parseInt(limit, 10) || 8));
      const input: Record<string, unknown> = {
        organizationID,
        query: queryText.trim(),
        limit: lim,
        filterMode,
        libraryIds,
        useHybridSearch,
      };
      if (objectTypesForRequest.length > 0) {
        input.objectTypes = objectTypesForRequest;
      }
      if (productSearch && productOptionsJson) {
        input.productOptions = productOptionsJson;
      }

      const result = await getGQLAPI().graphql({
        query: commerceSearchQuery,
        variables: { input },
        authMode: GRAPHQL_AUTH_MODE.AMAZON_COGNITO_USER_POOLS,
      }) as {
        errors?: unknown[];
        data?: { commerceSearch?: { formattedText: string; references: string } };
      };

      if (result?.errors?.length) {
        setError(formatError(result.errors));
        return;
      }
      const d = result?.data?.commerceSearch;
      if (!d) {
        setError('No result');
        return;
      }
      let refs: unknown[] = [];
      try {
        refs = JSON.parse(d.references) as unknown[];
        if (!Array.isArray(refs)) refs = [];
      } catch {
        refs = [];
      }
      setRawResult({ references: refs, formattedText: d.formattedText ?? '' });
    } catch (e) {
      setError(formatError(e));
    } finally {
      setLoading(false);
    }
  }, [
    organizationID,
    queryText,
    productSearch,
    objectTypesForRequest,
    productOptionsJson,
    limit,
    filterMode,
    libraryIds,
    useHybridSearch,
    libraryModeBlocked,
  ]);

  const addOptionRow = () => setOptionRows((rows) => [...rows, { name: '', value: '' }]);
  const removeOptionRow = (i: number) => {
    setOptionRows((rows) => (rows.length <= 1 ? rows : rows.filter((_, j) => j !== i)));
  };
  const setOptionRow = (i: number, field: keyof OptionPair, v: string) => {
    setOptionRows((rows) => rows.map((row, j) => (j === i ? { ...row, [field]: v } : row)));
  };

  return (
    <Box sx={{ maxWidth: 960 }}>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        GraphQL <code>commerceSearch</code> — same pipeline as Bedrock <strong>directSearch</strong>: Titan embed → vector similarity
        {useHybridSearch ? ' + hybrid FTS (RRF)' : ' only'}
        . Org <code>{organizationID || '—'}</code>. Mirrors portal Tools → Knowledge base:
        filter <Chip size="small" label={filterMode} sx={{ verticalAlign: 'middle', mx: 0.5 }} />
        hybrid <Chip size="small" label={String(useHybridSearch)} sx={{ verticalAlign: 'middle', mx: 0.5 }} />
      </Typography>

      <Stack spacing={2} sx={{ mb: 2 }}>
        <TextField
          label="Search query"
          value={queryText}
          onChange={(e) => setQueryText(e.target.value)}
          fullWidth
          multiline
          minRows={2}
          placeholder="Natural language (required — query is embedded)"
          required
          helperText="Same as the model’s search query string."
        />

        <FormControlLabel
          control={(
            <Checkbox
              checked={productSearch}
              onChange={(e) => setProductSearch(e.target.checked)}
            />
          )}
          label="Product search"
        />
        {productSearch && (
          <Typography variant="body2" color="text.secondary">
            Restricts results to <code>object_type = Product</code>. Narrow with query text and product options below.
          </Typography>
        )}

        {!productSearch && (
          <TextField
            select
            label="Entity type filter (optional)"
            value={entityType}
            onChange={(e) => setEntityType(e.target.value)}
            fullWidth
            helperText="Narrows to one KB object_type (Course, WebPage, …). Leave as Any to search all types."
          >
            {ENTITY_TYPES.map((t) => (
              <MenuItem key={t || 'any'} value={t}>
                {t || 'Any'}
              </MenuItem>
            ))}
          </TextField>
        )}

        {productSearch && (
          <Box>
            <Typography variant="subtitle2" sx={{ mb: 1 }}>
              Product options (name / value)
            </Typography>
            <Stack spacing={1}>
              {optionRows.map((row, i) => (
                <Stack key={i} direction="row" spacing={1} alignItems="center">
                  <TextField
                    size="small"
                    label="Name"
                    value={row.name}
                    onChange={(e) => setOptionRow(i, 'name', e.target.value)}
                    placeholder="e.g. Size or custom.gender"
                    sx={{ flex: 1 }}
                  />
                  <TextField
                    size="small"
                    label="Value"
                    value={row.value}
                    onChange={(e) => setOptionRow(i, 'value', e.target.value)}
                    sx={{ flex: 1 }}
                  />
                  <IconButton
                    aria-label="Remove row"
                    onClick={() => removeOptionRow(i)}
                    size="small"
                    disabled={optionRows.length <= 1}
                  >
                    <Delete />
                  </IconButton>
                </Stack>
              ))}
              <Button size="small" startIcon={<Add />} onClick={addOptionRow}>
                Add option pair
              </Button>
            </Stack>
          </Box>
        )}

        <TextField
          label="Max results"
          type="number"
          value={limit}
          onChange={(e) => setLimit(e.target.value)}
          inputProps={{ min: 1, max: 100 }}
          sx={{ maxWidth: 160 }}
        />

        <Box>
          <Button
            variant="contained"
            onClick={() => void runSearch()}
            disabled={loading || !canSearch}
          >
            {loading ? <CircularProgress size={22} color="inherit" /> : 'Search'}
          </Button>
        </Box>
      </Stack>

      {error && (
        <Typography color="error" sx={{ mb: 2 }}>
          {error}
        </Typography>
      )}

      {rawResult && (
        <Stack spacing={2}>
          <Paper variant="outlined" sx={{ p: 2, overflow: 'auto', maxHeight: 400 }}>
            <Typography variant="subtitle2" gutterBottom>
              References ({rawResult.references.length})
            </Typography>
            <Box
              component="pre"
              sx={{
                m: 0,
                fontSize: 12,
                whiteSpace: 'pre-wrap',
                wordBreak: 'break-word',
                fontFamily: 'monospace',
              }}
            >
              {JSON.stringify(rawResult.references, null, 2)}
            </Box>
          </Paper>
          {rawResult.formattedText ? (
            <Paper variant="outlined" sx={{ p: 2, overflow: 'auto', maxHeight: 280 }}>
              <Typography variant="subtitle2" gutterBottom>
                Formatted text (tool output shape)
              </Typography>
              <Box component="pre" sx={{ m: 0, fontSize: 11, whiteSpace: 'pre-wrap', fontFamily: 'monospace' }}>
                {rawResult.formattedText}
              </Box>
            </Paper>
          ) : null}
        </Stack>
      )}
    </Box>
  );
};

export default PgVectorSearchPanel;
